/**
 * Keyboard behaviour for the CRM's own pop-up windows (`.modal-backdrop` overlays): Escape closes the top one (as a click
 * on its backdrop does), focus moves into a window when it opens, Tab stays inside it, and focus goes back to where it
 * was when it closes. Native <dialog> windows already do all of this and are left alone.
 */
const FOCUSABLE = 'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';
const topBackdrop = () => { const all = document.querySelectorAll<HTMLElement>('.modal-backdrop'); return all[all.length - 1] ?? null; };
const focusables = (root: HTMLElement) => [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);

export function installModalA11y() {
  const returnTo = new Map<HTMLElement, Element | null>();

  document.addEventListener('keydown', (e) => {
    const top = topBackdrop();
    if (!top || document.querySelector('dialog[open]')) return;
    if (e.key === 'Escape') {
      if (e.defaultPrevented) return; // handled inside (an inline editor's own Escape)
      e.preventDefault();
      for (const type of ['mousedown', 'mouseup', 'click']) top.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
    } else if (e.key === 'Tab') {
      const items = focusables(top);
      if (!items.length) return;
      const first = items[0]!, last = items[items.length - 1]!;
      if (!top.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  new MutationObserver((records) => {
    for (const r of records) {
      r.addedNodes.forEach((n) => {
        if (!(n instanceof HTMLElement)) return;
        const bd = n.matches('.modal-backdrop') ? n : n.querySelector<HTMLElement>('.modal-backdrop');
        if (!bd || returnTo.has(bd)) return;
        returnTo.set(bd, document.activeElement);
        // After React's autoFocus: only when nothing inside has focus yet.
        requestAnimationFrame(() => { if (!bd.contains(document.activeElement)) focusables(bd)[0]?.focus(); });
      });
      r.removedNodes.forEach((n) => {
        if (!(n instanceof HTMLElement)) return;
        for (const [bd, prev] of returnTo) {
          if (bd === n || n.contains(bd)) {
            returnTo.delete(bd);
            if (prev instanceof HTMLElement && prev.isConnected) prev.focus();
          }
        }
      });
    }
  }).observe(document.body, { childList: true, subtree: true });
}
